// src/LanguageConfig.tsx
import React from 'react'
import { Tag } from 'antd'

export interface LanguageConfig {
  baseLanguages: string[]
  betaLanguages: string[]
}

export type LanguageReleaseStatus = 'public' | 'beta'

export interface LanguageCatalogEntry {
  name: string
  status: LanguageReleaseStatus
}

export interface QuotationOption {
  value: string
  label: string
  marks: string
  isDefault?: boolean
}

export interface QuotationStyle {
  options?: QuotationOption[]
  fixed?: {
    name: string
    marks: string
  }
}

// Canonical language catalog. Release status is data rather than array
// membership so all languages can graduate in one reversible launch switch.
export const LANGUAGE_CATALOG: LanguageCatalogEntry[] = [
  ...[
    'Dutch', 'English', 'British English', 'French', 'German', 'Italian',
    'European Portuguese', 'Brazilian Portuguese', 'European Spanish',
    'Latin American Spanish', 'Polish', 'Ukrainian', 'Swedish', 'Russian',
    'Czech', 'Danish', 'Bosnian', 'Croatian', 'Finnish', 'Icelandic', 'Bulgarian',
  ].map(name => ({ name, status: 'public' as const })),
  ...[
    'Japanese', 'Korean', 'Traditional Chinese', 'Simplified Chinese', 'Thai',
    'Hindi', 'Catalan', 'Afrikaans', 'Hebrew', 'Norwegian', 'Serbian', 'Turkish',
  ].map(name => ({ name, status: 'beta' as const })),
]

// Launch dates control presentation only; they do not grant language access.
export const NEW_LANGUAGE_BADGE_DURATION_DAYS = 30
const LANGUAGE_LAUNCH_DATES: Record<string, string> = {
  Czech: '2026-11-03T00:00:00Z',
  Danish: '2026-11-03T00:00:00Z',
  Bosnian: '2026-11-03T00:00:00Z',
  Croatian: '2026-11-03T00:00:00Z',
  Finnish: '2026-11-03T00:00:00Z',
  Icelandic: '2026-11-03T00:00:00Z',
  Bulgarian: '2026-11-03T00:00:00Z',
}

export const isLanguageNew = (language: string, now = Date.now()): boolean => {
  const launchedAt = Date.parse(LANGUAGE_LAUNCH_DATES[language] || '')
  const expiresAt = launchedAt + NEW_LANGUAGE_BADGE_DURATION_DAYS * 24 * 60 * 60 * 1000
  return now >= launchedAt && now < expiresAt
}

export const buildLanguageConfig = (
  releaseMode: 'catalog' | 'public' = 'catalog'
): LanguageConfig => {
  const entries = LANGUAGE_CATALOG.map(entry => ({
    ...entry,
    status: releaseMode === 'public' ? 'public' as const : entry.status,
  }))
  return {
    baseLanguages: entries.filter(entry => entry.status === 'public').map(entry => entry.name),
    betaLanguages: entries.filter(entry => entry.status === 'beta').map(entry => entry.name),
  }
}

const languageReleaseMode = process.env.REACT_APP_LANGUAGE_RELEASE_MODE === 'public'
  ? 'public'
  : 'catalog'

export const LANGUAGE_CONFIG: LanguageConfig = buildLanguageConfig(languageReleaseMode)

// Centralized quotation style configuration
export const QUOTATION_STYLES: Record<string, QuotationStyle> = {
  Czech: { options: [
    { value: 'czech quotes', label: 'Czech quotes', marks: '„ “', isDefault: true },
    { value: 'czech en dash', label: 'En dash', marks: '–' },
  ] },
  Danish: { options: [
    { value: 'danish en dash', label: 'En dash', marks: '–', isDefault: true },
    { value: 'danish guillemets', label: 'Guillemets', marks: '» «' },
  ] },
  Bosnian: { options: [
    { value: 'bosnian quotes', label: 'Bosnian quotes', marks: '„ ”', isDefault: true },
    { value: 'bosnian en dash', label: 'En dash', marks: '–' },
  ] },
  Croatian: { options: [
    { value: 'croatian quotes', label: 'Croatian quotes', marks: '„ ”', isDefault: true },
    { value: 'croatian en dash', label: 'En dash', marks: '–' },
  ] },
  Finnish: { options: [
    { value: 'finnish en dash', label: 'En dash', marks: '–', isDefault: true },
    { value: 'finnish quotes', label: 'Finnish quotes', marks: '” ”' },
  ] },
  Icelandic: { options: [
    { value: 'icelandic quotes', label: 'Icelandic quotes', marks: '„ “', isDefault: true },
    { value: 'icelandic en dash', label: 'En dash', marks: '–' },
  ] },
  Bulgarian: { options: [
    { value: 'bulgarian em dash', label: 'Em dash', marks: '—', isDefault: true },
    { value: 'bulgarian quotes', label: 'Bulgarian quotes', marks: '„ “' },
  ] },
  Afrikaans: {
    fixed: {
      name: 'Double Quotes',
      marks: '""',
    },
  },
  Korean: {
    fixed: {
      name: 'Double Quotes',
      marks: '""',
    },
  },
  Hebrew: {
    fixed: {
      name: 'Double Quotes',
      marks: '""',
    },
  },
  Serbian: {
    options: [
      {
        value: 'Serbian em dash',
        label: 'Em Dash',
        marks: '—',
        isDefault: true,
      },
      {
        value: 'Serbian quotes',
        label: 'Serbian Quotes',
        marks: '„ ”',
      },
    ],
  },
  Norwegian: {
    options: [
      {
        value: 'Norwegian replikkstrek',
        label: 'Replikkstrek (En Dash)',
        marks: '–',
        isDefault: true,
      },
      {
        value: 'Norwegian guillemets',
        label: 'Guillemets',
        marks: '« »',
      },
    ],
  },
  German: {
    options: [
      {
        value: 'german guillemets',
        label: 'Guillemets',
        marks: '» «',
        isDefault: true,
      },
      {
        value: 'german quotation marks',
        label: 'German Quotation Marks',
        marks: '„ "',
      },
    ],
  },
  French: {
    options: [
      {
        value: 'french em dash',
        label: 'Em Dash',
        marks: '—',
        isDefault: true,
      },
      {
        value: 'french guillemets',
        label: 'Guillemets',
        marks: '« »',
      },
    ],
  },
  Polish: {
    options: [
      {
        value: 'polish em dash',
        label: 'Em Dash',
        marks: '—',
		isDefault: true,
      },
      {
        value: 'polish quotes',
        label: 'Polish Quotation Marks',
        marks: '„ "',
      },	  
    ],
  },
  Swedish: {
    options: [
      {
        value: 'swedish quotes',
        label: 'Double Quotes',
        marks: '""',
        isDefault: true,
      },
      {
        value: 'swedish en dash',
        label: 'En Dash',
        marks: '–',
      },
    ],
  },  
  Ukrainian: {
    fixed: {
      name: 'Guillemets',
      marks: '« »',
    },
  },  
  English: {
    fixed: {
      name: 'Double Quotes',
      marks: '""',
    },
  },
  'British English': {
    fixed: {
      name: 'British Curly Single Quotes',
      marks: '‘ ’',
    },
  },
  Russian: {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },  
  Italian: {
    fixed: {
      name: 'Caporali',
      marks: '« »',
    },
  },
  Dutch: {
    fixed: {
      name: 'Single Quotation Marks',
      marks: "''",
    },
  },
  Japanese: {
    fixed: {
      name: 'Corner Brackets',
      marks: '「」',
    },
  },
  'Traditional Chinese': {
    fixed: {
      name: 'Corner Brackets',
      marks: '「」',
    },
  },
  'Simplified Chinese': {
    fixed: {
      name: 'Corner Brackets',
      marks: '「」',
    },
  },
  Thai: {
    fixed: {
      name: 'Double Quotation Marks',
      marks: '""',
    },
  },
  Hindi: {
    fixed: {
      name: 'Double Quotation Marks',
      marks: '""',
    },
  },
  'European Spanish': {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },
  'Latin American Spanish': {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },
  Catalan: {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },
  'European Portuguese': {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },
  'Brazilian Portuguese': {
    fixed: {
      name: 'Em Dash',
      marks: '—',
    },
  },
  Turkish: {
    fixed: {
      name: 'Double Quotes',
      marks: '""',
    },
  },
}

/** Return all available languages based on user access level */
export const getAllLanguages = (isBetaUser: boolean): string[] => {
  return isBetaUser
    ? [...LANGUAGE_CONFIG.baseLanguages, ...LANGUAGE_CONFIG.betaLanguages]
    : LANGUAGE_CONFIG.baseLanguages
}

/** Check if a given language is in beta */
export const isLanguageBeta = (language: string): boolean => {
  return LANGUAGE_CONFIG.betaLanguages.includes(language)
}

/** Get a React node for display, with a styled "BETA" Tag when appropriate */
export const getLanguageDisplayName = (language: string): React.ReactNode => {
  const isBeta = isLanguageBeta(language)

  return (
    <span>
      {language}
      {isBeta && (
        <Tag
          color="orange"
          style={{
            marginLeft: 8,
            fontSize: '0.75em',
            padding: '0 4px',
            lineHeight: 1,
          }}
        >
          BETA
        </Tag>
      )}
    </span>
  )
}

/** Fallback plain-text display name for environments that can't render React */
export const getLanguageDisplayText = (language: string): string => {
  return isLanguageBeta(language) ? `${language} (BETA)` : language
}

/** Get quotation style configuration for a language */
export const getQuotationStyle = (language: string): QuotationStyle | undefined => {
  return QUOTATION_STYLES[language]
}

/** Check if a language has selectable quotation options */
export const hasQuotationOptions = (language: string): boolean => {
  const style = getQuotationStyle(language)
  return !!(style && style.options && style.options.length > 0)
}

/** Get default quotation style for a language */
export const getDefaultQuotationStyle = (language: string): string => {
  const style = getQuotationStyle(language)
  if (style?.options) {
    const defaultOption = style.options.find(opt => opt.isDefault)
    return defaultOption ? defaultOption.value : style.options[0].value
  }
  return ''
}

/** Get tooltip text for quotation style */
export const getQuotationTooltip = (language: string): string => {
  const baseText = "Determines how quotations appear in the translated text. Different languages have different conventions for marking direct speech and quotes."
  
  if (language === 'German') {
    return `${baseText} For German, we recommend guillemets (» «) for most genres.`
  }
  if (language === 'French') {
    return `${baseText} For French, we recommend em dashes (—) for most genres. French also supports guillemets (« »).`
  }
  if (language === 'Polish') {
    return `${baseText} For Polish, we recommend em dashes (—) for most genres.`
  }  
  if (language === 'Swedish') {
    return `${baseText} For Swedish, we recommend double quotes ("") for most genres.`
  }  
  return baseText
}

/** Create a standardized Beta tag component */
export const BetaTag: React.FC = () => (
  <Tag
    color="orange"
    style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}
  >
    BETA
  </Tag>
)
